import { Node, Edge, addEdge } from "reactflow";
import utils from "core/utils";
import componentService from "core/services/componentService/component-service";
import cqTransformationProvider from "app/components/common/editor/meta-object-editors/cqTransformations/transformationProvider";
import { EdgeType } from "../flow/flow.constants";
import { Layouts, type Layout } from "../helpers/flow.constants";

const getAutoPropertiesForStreamChild = (nodeType: string, parentMetaObject: any = {}, componentName?: string) => {
    const parentNodeId = parentMetaObject?.id;
    const parentNodeName = parentMetaObject?.name;

    switch (nodeType) {
        case utils.ENTITIES.WINDOW:
            return parentNodeId ? { stream: parentNodeId } : {};
        case utils.ENTITIES.TARGET:
            return parentNodeId ? { inputStream: parentNodeId } : {};
        case utils.ENTITIES.ROUTER:
            return parentNodeId ? { inStream: parentNodeId } : {};
        case utils.ENTITIES.SENTINEL:
            return parentNodeId ? { inputStream: parentNodeId } : {};
        case utils.ENTITIES.CQ: {
            const isCqTransformation = componentName && cqTransformationProvider.getTransformationByName(componentName);
            if (isCqTransformation) {
                return parentNodeId ? { uiconfig: { subType: componentName, config: { stream: parentNodeId } } } : {};
            }
            return parentNodeName
                ? { select: `SELECT * FROM ${parentNodeName} ${parentNodeName.charAt(0).toLowerCase()};` }
                : {};
        }
        default:
            return {};
    }
};

interface HandleConnectParams {
    selectedOption: { label: string; value: string };
    parentNode: Node;
    nodeId: string;
    data: Record<string, any>;
    nodes: Node[];
    edges: Edge[];
    currentLayout: Layout;
}

interface HandleConnectResult {
    layoutedNodes: Node[];
    layoutedEdges: Edge[];
    newNodeId: string;
}

const handleConnect = ({
    selectedOption,
    parentNode,
    nodeId,
    data,
    nodes,
    edges,
    currentLayout
}: HandleConnectParams): HandleConnectResult | null => {
    if (!selectedOption || !parentNode) {
        return null;
    }

    const parentMetaObject = data?.metaObject || parentNode.data?.metaObject || {};
    const componentId =
        componentService.getComponentIDFromName(selectedOption.label) ||
        componentService.getComponentIDFromName(selectedOption.value);
    const newNodeId = `temp-${selectedOption.value.toLowerCase()}-${Date.now()}`;
    const displayName = `New ${selectedOption.label}`;
    const autoProperties = getAutoPropertiesForStreamChild(selectedOption.value, parentMetaObject, selectedOption.label);
    const newNode: Node = {
        id: newNodeId,
        type: selectedOption.value,
        position:
            currentLayout === Layouts.HORIZONTAL
                ? { x: parentNode.position.x + 280, y: parentNode.position.y }
                : { x: parentNode.position.x + 150, y: parentNode.position.y + 130 },
        data: {
            label: displayName,
            type: selectedOption.value,
            componentType: selectedOption.value,
            name: displayName,
            componentId,
            metaObjectId: "temp-node",
            targets: [],
            metaObject: {
                type: selectedOption.value,
                name: displayName,
                ...(Object.keys(autoProperties).length > 0 ? { autoProperties } : {})
            }
        }
    };

    const updatedNodes = [...nodes, newNode];
    const updatedEdges = addEdge(
        {
            source: nodeId,
            sourceHandle: null,
            target: newNodeId,
            targetHandle: null,
            type: EdgeType
        },
        edges
    );

    return { layoutedNodes: updatedNodes, layoutedEdges: updatedEdges, newNodeId };
};

export { getAutoPropertiesForStreamChild, handleConnect };
export type { HandleConnectParams, HandleConnectResult };
